import { action, computed, makeObservable, observable } from 'mobx'

import { IGeoJson2DGeographicCoordinates } from '~/client/graph'
import { IMarkupFeatureData } from '~/client/src/shared/components/MapMarkups/MarkupDrawController'
import {
  IMarkupEditorHandles,
  MarkupLocationSnapshot,
} from '~/client/src/shared/components/MapMarkups/MarkupEditorStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { isMapAnimationSuspended } from '~/client/src/shared/map/mapCaptureFrame'
import Markup from '~/client/src/shared/models/Markup'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { toast } from '~/client/src/shared/utils/toast'

import MapBoxViewerStore from '../MapBoxViewer/MapBoxViewer.store'
import { MarkupEditorTextChannel } from '../MapMarkups/markupEditorTextChannel'

export type MapMarkupSessionPhase = 'idle' | 'active' | 'submitting'
export type MapMarkupSessionOutcome = 'discard' | 'complete'

// flyTo's mercator round-trip leaves sub-pixel drift in the reported center, so
// "did the pin move" needs a tolerance above that yet far below a real pan (~11cm).
const COORD_MOVE_EPSILON = 1e-6

// `coordinates` is a flat vertex list for every markup kind, so one min/max pass
// covers rect/line/freehand alike; a single-vertex text degenerates to the point.
function boundingBoxCenter(
  coords: IGeoJson2DGeographicCoordinates[] | undefined,
): IGeoJson2DGeographicCoordinates | null {
  if (!coords?.length) {
    return null
  }
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (const { latitude, longitude } of coords) {
    if (latitude < minLat) minLat = latitude
    if (latitude > maxLat) maxLat = latitude
    if (longitude < minLng) minLng = longitude
    if (longitude > maxLng) maxLng = longitude
  }
  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
  }
}

// Always-on markup session (STX1-686); a creation begins on demand (bubble or first
// markup), buffering markups under a temp id flushed to the real id on create.
export default class MapMarkupSessionStore {
  @observable public phase: MapMarkupSessionPhase = 'idle'
  // The session's text in GL: the editor's while one is mounted, the drafts' otherwise.
  public readonly editorText = new MarkupEditorTextChannel()
  // The editor's dirty flag against an empty baseline (falls on undo-to-zero);
  // drawing is optional — a pin-only workflow can still submit.
  @observable public hasDrawnMarkups: boolean = false
  // Non-null while creating; anchors the pin and the pin-glued Composer.
  @observable.ref
  public creationCoordinates: IGeoJson2DGeographicCoordinates = null
  // The camera the pin was dropped under. Held because the Composer opens later and may open
  // over a resized map, and the record's orientation must be the one the user was looking at.
  @observable.ref
  public creationCamera: { bearing: number; pitch: number } = null
  // On the session, not the Composer: it outlives a mid-submit remount.
  @observable public creationWorkflowTypeId: string = null
  // Not a phase: that trips the `phase !== 'active'` guards in commitDrafts and
  // handleEditorDirtyChange, so post-hand-off strokes would register nothing.
  @observable public isHandedOff: boolean = false
  // Not creationCoordinates: that drives isCreating and would bring the composer back.
  @observable.ref
  public handedOffCoordinates: IGeoJson2DGeographicCoordinates = null
  // handOffCreation clears creationWorkflowTypeId; the pin still shows this type.
  @observable public handedOffWorkflowTypeId: string = null
  // Remounts the editor: an in-place reload re-adopts drafts the form hasn't flushed yet.
  @observable public editorEpoch: number = 0
  public applyHandedOffLocation: (
    coords: IGeoJson2DGeographicCoordinates,
  ) => void = null
  // Mirrors the editor's idle-select state (Select tool, nothing selected). The
  // Composer is only shown while idle so it never covers active drawing/editing.
  @observable public isEditorIdle: boolean = true
  // Mirrors the editor's text-subject state: a Text markup being typed into or
  // selected (its context toolbar shown). Keeps the peek re-center off the pin.
  @observable public isTextSubject: boolean = false
  // Reposition mode (the Precise Location pan-under-a-fixed-pin model).
  @observable public isMovingPin: boolean = false
  // User chose the pin position (dropped or moved), so the first-markup recompute
  // must not override it; false only for a markup-first creation.
  private pinIsExplicit = false
  // Tracked by id, not array position: getEditorFeatures lists geometry before
  // text, so a later geometry would seize [0] and steal a text-first pin.
  private pinAnchorFeatureId: string = null
  // Pin coordinate captured when reposition starts, restored on Cancel.
  private preMoveCoordinates: IGeoJson2DGeographicCoordinates = null
  public sessionPermitId: string = null

  public onCreationStart: () => void = null
  public onCreationEnd: (outcome: MapMarkupSessionOutcome) => void = null
  // Read by onCreationEnd('complete') to select the newly created workflow.
  public lastCreatedPermitId: string = null
  // Captured on complete before creationCoordinates clears, so the map can
  // recenter on the created workflow even if the user panned away mid-creation.
  public lastCreatedCoordinates: IGeoJson2DGeographicCoordinates = null
  // Fired after a pin move so the Composer re-snaps its location and re-anchors.
  public onCoordinatesChange: () => void = null

  private editorSave: () => void = null
  private editorDiscard: () => void = null
  private editorRecordLocationChange: () => void = null
  private editorResetBaseline: () => void = null
  private editorExitDrawing: () => void = null
  private getEditorFeatures: () => IMarkupFeatureData[] = null

  public constructor(
    private readonly markupsStore: MarkupsStore,
    public readonly mapBoxViewerStore: MapBoxViewerStore,
    private readonly getProjectId: () => string,
  ) {
    makeObservable(this)
  }

  @computed
  public get isActive(): boolean {
    return this.phase !== 'idle'
  }

  @computed
  public get isEditorMounted(): boolean {
    return this.phase === 'active'
  }

  @computed
  public get isCreating(): boolean {
    return !!this.creationCoordinates
  }

  @computed
  public get pinCoordinates(): IGeoJson2DGeographicCoordinates {
    return this.creationCoordinates ?? this.handedOffCoordinates
  }

  // Committed drafts count too: after a failed submit the remounted editor treats
  // them as its clean baseline, yet a discard would still drop them; while
  // submitting they're the in-flight create's hand-off — nothing at risk.
  @computed
  public get hasUnsavedMarkups(): boolean {
    return (
      (this.hasDrawnMarkups || this.draftMarkups.length > 0) &&
      this.phase !== 'submitting'
    )
  }

  // The guard confirms whenever a discard would lose the draft workflow; false
  // while submitting — the drafts belong to the in-flight create then.
  @computed
  public get hasGuardedWork(): boolean {
    return this.isCreating && this.phase !== 'submitting'
  }

  // Map create gestures and browse chrome (swipeable cards, right-click pin,
  // "+ Create new") step aside while the user is mid-interaction.
  @computed
  public get isInteracting(): boolean {
    return this.isCreating || !this.isEditorIdle
  }

  public get projectId(): string {
    return this.getProjectId()
  }

  // Pending drafts under the temp id; populated after commitDrafts().
  @computed
  public get draftMarkups(): Markup[] {
    return this.sessionPermitId
      ? this.markupsStore.getBySitePermitId(this.sessionPermitId)
      : []
  }

  @action.bound
  public startSession(): void {
    if (this.phase !== 'idle') {
      return
    }
    this.sessionPermitId = BsonObjectId.generate()
    this.phase = 'active'
  }

  @action.bound
  public setCreationWorkflowType(typeId: string): void {
    this.creationWorkflowTypeId = typeId
  }

  /** `isExplicit` = the user dropped the pin, which then stays put; false for a
   *  markup-first start, whose pin follows the first markup until it's moved. */
  @action.bound
  public startCreation(
    coords: IGeoJson2DGeographicCoordinates,
    isExplicit: boolean,
    camera?: { bearing: number; pitch: number },
  ): void {
    if (this.phase === 'idle' || this.isCreating || !coords) {
      return
    }
    this.creationCoordinates = coords
    this.creationCamera = camera ?? null
    this.pinIsExplicit = isExplicit
    // Show markups for the rest of this map view: context while drawing, and the
    // committed result stays visible after submit. Held until unmount, so it never
    // touches the stored preference.
    this.mapBoxViewerStore.markupPrefs.forceWorkflowMarkupsVisible()
    // Re-baseline the editor's undo history here so a later pin move is a single
    // clean undo step back, with no stale entry from a previous creation beneath.
    this.editorResetBaseline?.()
    this.onCreationStart?.()
  }

  // With no creation yet this is a first-class entry point: Done starts the creation
  // at the map center (preMoveCoordinates stays null → Cancel just exits).
  @action.bound
  public startMovingPin(): void {
    this.isMovingPin = true
    this.preMoveCoordinates = this.pinCoordinates
    this.mapBoxViewerStore.focusMapCanvas()
    if (this.pinCoordinates) {
      const { latitude, longitude } = this.pinCoordinates
      // flyTo, not the controlled viewport prop (see cancelMovingPin).
      this.mapBoxViewerStore.mapRef?.flyTo({
        center: [longitude, latitude],
        essential: true,
      })
    }
  }

  // Restores the pre-move pin without re-snapping the location field.
  @action.bound
  public cancelMovingPin(): void {
    if (!this.isMovingPin) {
      return
    }
    this.isMovingPin = false
    if (this.preMoveCoordinates) {
      if (this.isHandedOff) {
        this.handedOffCoordinates = this.preMoveCoordinates
      } else {
        this.creationCoordinates = this.preMoveCoordinates
      }
      const { latitude, longitude } = this.preMoveCoordinates
      // flyTo, not the controlled viewport prop: repeated setViewport reconciliations
      // get dropped mid-session, so a second cancel wouldn't recenter.
      this.mapBoxViewerStore.mapRef?.flyTo({
        center: [longitude, latitude],
        essential: true,
      })
    }
  }

  // The map center becomes the new pin; with no creation yet it starts one there.
  @action.bound
  public finishMovingPin(): void {
    if (!this.isMovingPin) {
      return
    }
    const { latitude, longitude } = this.mapBoxViewerStore.viewport
    this.isMovingPin = false
    // The form owns this creation: never start a new one.
    if (this.isHandedOff) {
      this.handedOffCoordinates = { latitude, longitude }
      this.applyHandedOffLocation?.(this.handedOffCoordinates)
      return
    }
    if (!this.creationCoordinates) {
      this.startCreation({ latitude, longitude }, true)
      return
    }
    const moved =
      !this.preMoveCoordinates ||
      Math.abs(latitude - this.preMoveCoordinates.latitude) >
        COORD_MOVE_EPSILON ||
      Math.abs(longitude - this.preMoveCoordinates.longitude) >
        COORD_MOVE_EPSILON
    this.creationCoordinates = { latitude, longitude }
    // An explicit move cuts the markup→pin link for good (Scenario B → explicit).
    this.pinIsExplicit = true
    this.onCoordinatesChange?.()
    // One unified undo step for the move — the pre-move position is already the
    // history head (creation baseline, or the previously recorded state).
    if (moved) {
      this.editorRecordLocationChange?.()
    }
  }

  // Lift the pin into the map visible above the mobile sheet (else it hides
  // under the form).
  public centerCreationPinInView(sheetTopY: number): void {
    // A Text subject (typed into OR selected — the box stays selected right after
    // finishing) skips the recenter; a drawing tool still frames the pin on peek.
    if (!this.isCreating || this.isTextSubject) {
      return
    }
    this.centerCoordinatesAboveSheet(this.creationCoordinates, sheetTopY)
  }

  // Also frames the type picker's seed, before any creation exists. One-shot offset,
  // not padding, so move/undo's fixed center holds.
  public centerCoordinatesAboveSheet = (
    { latitude, longitude }: IGeoJson2DGeographicCoordinates,
    sheetTopY: number,
  ): void => {
    const mapRef = this.mapBoxViewerStore.mapRef
    // A capture holds the map: an ease started under it lands in the report shot.
    if (!mapRef || sheetTopY < 0 || isMapAnimationSuspended(mapRef.getMap())) {
      return
    }
    const rect = mapRef.getContainer().getBoundingClientRect()
    // Clamp the strip top to 0 — a PWA scrolls the full-height container up under
    // the keyboard (rect.top < 0); the ease offset is relative to the container center.
    const stripTop = Math.max(0, rect.top)
    const stripBottom = Math.min(sheetTopY, rect.bottom)
    const offsetY = Math.round(
      (stripTop + stripBottom) / 2 - (rect.top + rect.height / 2),
    )
    mapRef.easeTo({
      center: [longitude, latitude],
      offset: [0, offsetY],
      essential: true,
      duration: 400,
    })
  }

  // `onlyIfOffscreen` (desktop) avoids a jarring recenter when the pin is already
  // visible; mobile always centers — header/card chrome can hide an in-bounds pin.
  public centerOnCreatedWorkflow(onlyIfOffscreen: boolean): void {
    const coords = this.lastCreatedCoordinates
    const mapRef = this.mapBoxViewerStore.mapRef
    if (!coords || !mapRef) {
      return
    }
    const center: [number, number] = [coords.longitude, coords.latitude]
    if (onlyIfOffscreen && mapRef.getMap()?.getBounds()?.contains(center)) {
      return
    }
    mapRef.flyTo({ center, essential: true })
  }

  @action.bound
  public handleIdleSelectChange(isIdle: boolean): void {
    this.isEditorIdle = isIdle
  }

  @action.bound
  public handleTextSubjectChange(isText: boolean): void {
    this.isTextSubject = isText
  }

  /** Persists live terra-draw content as pending drafts under the temp id; the
   *  caller creates the workflow, then flushDraftsTo(realId) + endCreation. */
  @action.bound
  public commitDrafts(): Markup[] {
    if (this.phase !== 'active') {
      return this.draftMarkups
    }
    // Submitting first: the editor save() below ends with deps.onClose, which
    // must be a no-op rather than a creation teardown.
    this.phase = 'submitting'
    this.editorSave?.()
    return this.draftMarkups
  }

  /** setEditingSitePermitId(realId) keeps the flushed markups alive while retention
   *  can transiently evict them, until the new workflow enters the filter set. */
  @action.bound
  public flushDraftsTo(realPermitId: string): void {
    this.markupsStore.setEditingSitePermitId(realPermitId)
    this.markupsStore.flushPending(this.sessionPermitId, realPermitId)
  }

  /** Create failed: back to the active editor, repopulated from the pending drafts. */
  @action.bound
  public resumeDrawing(): void {
    if (this.phase === 'submitting') {
      this.phase = 'active'
    }
  }

  /** The full form took this creation over (its type has a mandatory field the composer
   *  cannot fill). Mirrors endCreation, minus the discard: the form flushes these
   *  drafts. */
  @action.bound
  public handOffCreation(): void {
    if (!this.isCreating) {
      return
    }
    this.editorExitDrawing?.()
    // Only path that persists live terra-draw content, and the editor unmounts next.
    this.commitDrafts()
    // Held before the clear: the map keeps showing and moving this pin.
    this.handedOffCoordinates = this.creationCoordinates
    this.handedOffWorkflowTypeId = this.creationWorkflowTypeId
    this.creationCoordinates = null
    this.creationCamera = null
    this.creationWorkflowTypeId = null
    this.hasDrawnMarkups = false
    this.pinIsExplicit = false
    this.isMovingPin = false
    this.pinAnchorFeatureId = null
    this.isEditorIdle = true
    this.isTextSubject = false
    this.phase = 'active'
    // Not re-minted: the form adopted this id and later strokes must reach it.
    this.isHandedOff = true
    this.markupsStore.registerPendingCommitter(
      this.sessionPermitId,
      this.commitHandedOffDrafts,
    )
    // loadedIds date from mount; without a rebase a later delete is never removed.
    this.editorDiscard?.()
  }

  /** Unlike commitDrafts, keeps the phase: 'submitting' would unmount the editor for good. */
  @action.bound
  public commitHandedOffDrafts(): void {
    // The form's teardown may have discarded the buffer first (route change).
    if (
      !this.isHandedOff ||
      !this.markupsStore.hasPendingCommitter(this.sessionPermitId)
    ) {
      return
    }
    this.editorSave?.()
    // Rebase loadedIds on what was just saved, or a later delete is never removed.
    this.editorDiscard?.()
  }

  @action.bound
  public endHandOff(): void {
    if (!this.isHandedOff) {
      return
    }
    this.markupsStore.unregisterPendingCommitter(this.sessionPermitId)
    this.isHandedOff = false
    this.handedOffCoordinates = null
    this.handedOffWorkflowTypeId = null
    // Left on after the form closes, Done would start a new creation.
    this.isMovingPin = false
    this.preMoveCoordinates = null
    this.hasDrawnMarkups = false
    this.sessionPermitId = BsonObjectId.generate()
    this.editorEpoch++
  }

  /** Submit failed after the host unmounted (a view switch is allowed mid-submit),
   *  so there is no editor to resume into: drop the drafts buffered under the temp
   *  id instead of stranding them in the store. */
  @action.bound
  public abandonSubmission(): void {
    if (this.phase !== 'submitting') {
      return
    }
    this.phase = 'active'
    this.markupsStore.discardPending(this.sessionPermitId)
  }

  /** Returns to the always-on active state — the editor stays mounted; a fresh
   *  temp id lets the next creation start clean. */
  @action.bound
  public endCreation(outcome: MapMarkupSessionOutcome): void {
    if (!this.isCreating && this.phase !== 'submitting') {
      return
    }
    // A discard now would delete the in-flight create's committed drafts and re-mint the
    // temp id; it resolves via 'complete' or resumeDrawing instead.
    if (outcome === 'discard' && this.phase === 'submitting') {
      return
    }
    // Neither discardChanges nor save resets the tool, so it would stay armed.
    this.editorExitDrawing?.()
    if (outcome === 'discard') {
      // Drafts first: the editor's discard reloads from the store, and after a failed submit the
      // drafts are pending there, so reloading first would bring them back into the editor.
      this.markupsStore.discardPending(this.sessionPermitId)
      this.editorDiscard?.()
    }
    // Snapshot the pin before clearing so selectCreatedWorkflow can recenter on it.
    this.lastCreatedCoordinates =
      outcome === 'complete' ? this.creationCoordinates : null
    this.creationCoordinates = null
    this.creationCamera = null
    this.creationWorkflowTypeId = null
    this.hasDrawnMarkups = false
    this.isMovingPin = false
    this.pinIsExplicit = false
    this.pinAnchorFeatureId = null
    // Reset the editor mirrors or isInteracting sticks true after a create with
    // markups — the submitting editor unmounts, so its reactions can't reset them.
    this.isEditorIdle = true
    this.isTextSubject = false
    if (this.phase === 'submitting') {
      this.phase = 'active'
    }
    this.sessionPermitId = BsonObjectId.generate()
    this.onCreationEnd?.(outcome)
  }

  /** Host unmount: discard the creation — drafts must not leak (retention never
   *  evicts isDraft rows); while submitting they're the in-flight create's hand-off. */
  @action.bound
  public dispose(): void {
    if (this.phase === 'submitting') {
      return
    }
    // The form owns these drafts and discards them itself if abandoned.
    if (this.isHandedOff) {
      // Strokes since the hand-off exist only in the editor.
      this.commitHandedOffDrafts()
      this.markupsStore.unregisterPendingCommitter(this.sessionPermitId)
      this.isHandedOff = false
      this.handedOffCoordinates = null
      this.handedOffWorkflowTypeId = null
      this.isMovingPin = false
      this.preMoveCoordinates = null
    }
    if (this.isCreating) {
      // Reaching here with a live creation means nothing asked: every guarded path —
      // navigation, view switch, unload — calls endCreation itself before the host
      // unmounts. What is left is a forced teardown, such as the automatic fall back to
      // the list when the app loses its associated map, so say so rather than dropping
      // the drawing in silence.
      toast.info(Localization.translator.discardedUnsavedDrawing)
      this.endCreation('discard')
    }
    this.phase = 'idle'
    this.isEditorIdle = true
    this.isTextSubject = false
    this.editorSave = null
    this.editorDiscard = null
    this.editorExitDrawing = null
    this.getEditorFeatures = null
    // The last two kept pointing at the disposed editor and could stale-fire on
    // the next creation before the fresh editor re-wires (benign today, fragile).
    this.editorRecordLocationChange = null
    this.editorResetBaseline = null
  }

  // null on editor unmount — clears the handles so none stale-fire against a dead editor.
  @action.bound
  public handleEditorReady(handles: IMarkupEditorHandles | null): void {
    this.editorSave = handles?.save ?? null
    this.editorRecordLocationChange = handles?.recordLocationChange ?? null
    this.getEditorFeatures = handles?.getFeatures ?? null
    this.editorDiscard = handles?.discardChanges ?? null
    this.editorResetBaseline = handles?.resetLocationBaseline ?? null
    this.editorExitDrawing = handles?.exitDrawing ?? null
  }

  // Called when the composer re-expands; an explicit collapse must NOT call it —
  // that keeps the drawing session intact.
  public exitDrawing = (): void => {
    this.editorExitDrawing?.()
  }

  // Camera fields are fixed — only lat/lng matter, so markup-only edits don't
  // perturb the snapshot and trigger a spurious camera move on undo.
  public getLocationSnapshot = (): MarkupLocationSnapshot => {
    if (!this.creationCoordinates) {
      return null
    }
    const { latitude, longitude } = this.creationCoordinates
    return { latitude, longitude, bearing: 0, pitch: 0, zoom: 0 }
  }

  // Skipped while repositioning (the mode owns the pin) and on markup-only steps
  // (coordinate unchanged), so those undos don't jerk the camera.
  @action.bound
  public applyLocationSnapshot(loc: MarkupLocationSnapshot): void {
    if (this.isMovingPin || !loc) {
      return
    }
    const current = this.creationCoordinates
    if (
      current &&
      loc.latitude === current.latitude &&
      loc.longitude === current.longitude
    ) {
      return
    }
    this.creationCoordinates = {
      latitude: loc.latitude,
      longitude: loc.longitude,
    }
    this.mapBoxViewerStore.mapRef?.flyTo({
      center: [loc.longitude, loc.latitude],
      essential: true,
    })
    this.onCoordinatesChange?.()
  }

  @action.bound
  public handleEditorDirtyChange(isDirty: boolean): void {
    // The editor's save() (inside commitDrafts) resets its dirty flag; the
    // committed drafts still exist, so the creation stays "has markups".
    if (this.phase !== 'active') {
      return
    }
    // The markup-first branch below would open a second creation behind the form.
    if (this.isHandedOff) {
      this.hasDrawnMarkups = isDirty
      return
    }
    this.hasDrawnMarkups = isDirty
    // Undo-to-zero (isDirty false) keeps a pin-only creation alive.
    if (!isDirty) {
      return
    }
    // Center on the FIRST markup only (tracked by id, not index); stop once the
    // user moves the pin by hand.
    const features = this.getEditorFeatures?.() ?? []
    const anchored = this.pinAnchorFeatureId
      ? features.find(f => f.id === this.pinAnchorFeatureId)
      : null
    const source = anchored ?? features[0]
    const center = boundingBoxCenter(source?.coordinates)
    if (!center) {
      return
    }
    if (!this.isCreating) {
      // Markup-first: the pin is derived, so it follows this first markup.
      this.pinAnchorFeatureId = source?.id ?? null
      this.startCreation(center, false)
      // startCreation's re-baseline synchronously echoed clean and cleared the flag;
      // the markup still exists and a discard would lose it — keep the guard armed.
      this.hasDrawnMarkups = true
      return
    }
    // The anchored markup was deleted → re-anchor to the new first markup.
    if (!anchored) {
      this.pinAnchorFeatureId = source?.id ?? null
    }
    const prev = this.creationCoordinates
    const unchanged =
      prev &&
      prev.latitude === center.latitude &&
      prev.longitude === center.longitude
    if (this.pinIsExplicit || unchanged) {
      return
    }
    this.creationCoordinates = center
    this.onCoordinatesChange?.()
  }

  @action.bound
  public handleEditorClose(): void {
    // Only reachable via the editor's save(); during commitDrafts the creation
    // survives the editor teardown, and a handed-off buffer belongs to the form.
    if (this.phase === 'submitting' || this.isHandedOff) {
      return
    }
    this.endCreation('discard')
  }
}

// Structural guard type so callers can pass the WorkflowDrawingGuard store
// without coupling this module to it.
export function runWithMarkupSessionGuard(
  session: MapMarkupSessionStore,
  guard: { guard(proceed: () => void): void },
  proceed: () => void,
): void {
  // Submitting: the drafts belong to the in-flight create — discarding or
  // re-minting the session id here would strip the created workflow's markups.
  if (!session?.isCreating || session.phase === 'submitting') {
    return proceed()
  }
  guard.guard(proceed)
}
